import { json } from '@codemirror/lang-json';
import { Menu, MenuItem, type PopoverPosition, Stack } from '@mui/material';
import ReactCodeMirror, {
  type BasicSetupOptions,
  EditorView,
  type Extension,
  type ReactCodeMirrorRef,
} from '@uiw/react-codemirror';
import { useRef, useState } from 'react';

import { Text, isDefined, useCopyToClipboard } from 'common';

import { resolveCodeMirrorReferencePath } from './FieldReference/resolveCodeMirrorReferencePath';
import { EventAction, EventCategory, useCaptureEvents } from '../../../../../../../utils/events';

const jsonExtensions: Extension[] = [json()];
const wrappedJsonExtensions: Extension[] = [json(), EditorView.lineWrapping];

export interface OutputCodeMirrorProps {
  content: string;
  isFullHeightView: boolean;
  allowLineWrapping?: boolean;
  autoFocus?: boolean;
  basicSetup?: BasicSetupOptions;
  referenceRoot?: string;
}

interface FieldReferenceMenu {
  anchorPosition: PopoverPosition;
  reference: string;
}

export function OutputCodeMirror(props: OutputCodeMirrorProps) {
  const {
    content,
    isFullHeightView,
    allowLineWrapping = true,
    autoFocus = true,
    basicSetup,
    referenceRoot,
  } = props;
  const { copyToClipboard } = useCopyToClipboard();
  const { captureEvent } = useCaptureEvents();
  const codeMirrorReference = useRef<ReactCodeMirrorRef>(null);
  const [fieldReferenceMenu, setFieldReferenceMenu] = useState<FieldReferenceMenu>();

  return (
    <>
      <ReactCodeMirror
        ref={codeMirrorReference}
        autoFocus={autoFocus}
        basicSetup={basicSetup}
        extensions={allowLineWrapping ? wrappedJsonExtensions : jsonExtensions}
        value={content}
        editable={true}
        readOnly={true}
        minHeight={isFullHeightView ? '90vh' : '120px'}
        style={{
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.23)',
        }}
        onContextMenu={(event) => {
          const editorView = codeMirrorReference.current?.view;
          if (
            !isDefined(referenceRoot) ||
            !isDefined(editorView) ||
            editorView.state.selection.ranges.some((selectionRange) => !selectionRange.empty)
          ) {
            return;
          }

          const documentPosition = editorView.posAtCoords({
            x: event.clientX,
            y: event.clientY,
          });
          if (!isDefined(documentPosition)) {
            return;
          }

          const reference = resolveCodeMirrorReferencePath({
            state: editorView.state,
            position: documentPosition,
            rootPath: referenceRoot,
          });
          if (!isDefined(reference)) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          setFieldReferenceMenu({
            anchorPosition: { left: event.clientX, top: event.clientY },
            reference,
          });
          captureEvent({
            category: EventCategory.BlockConfiguration,
            action: EventAction.FieldReferenceMenuOpen,
          });
        }}
      />
      <Menu
        open={isDefined(fieldReferenceMenu)}
        onClose={() => setFieldReferenceMenu(undefined)}
        disableRestoreFocus
        anchorReference="anchorPosition"
        anchorPosition={fieldReferenceMenu?.anchorPosition}
        slotProps={{ paper: { sx: { width: 320 } } }}
      >
        <MenuItem
          sx={{ alignItems: 'flex-start', whiteSpace: 'normal' }}
          onClick={() => {
            if (isDefined(fieldReferenceMenu)) {
              copyToClipboard(fieldReferenceMenu.reference);
              captureEvent({
                category: EventCategory.BlockConfiguration,
                action: EventAction.CopyFieldReferenceMenuItemClick,
              });
              setFieldReferenceMenu(undefined);
            }
          }}
        >
          <Stack gap={0.25} minWidth={0}>
            <Text variant="body2" bold>
              Copy reference
            </Text>
            <Text variant="monospace" sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
              {fieldReferenceMenu?.reference}
            </Text>
          </Stack>
        </MenuItem>
      </Menu>
    </>
  );
}
